/**
 * Scheda giocatore — Attributi (V38).
 *
 * Ogni attributo si valuta da 1 a 100. Mister e Dirigente vedono le
 * misurazioni rilevate dallo staff delle loro squadre-stagione e scrivono solo
 * con un tesseramento in corso; l'Admin le legge tutte. Una misurazione nata
 * da un test dice test e risultato, rimanda all'esecuzione e si corregge solo
 * lì.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, OctagonXIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FAMIGLIE_ATTRIBUTO,
  misurazioneSchema,
  puo,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { provenienzaAParole } from '../test-misura/esecuzione.js';
import { CampoData } from '../comuni/CampoData.js';
import { coloreValore } from './colori-stato.js';
import {
  type AttributiGiocatore as Dati,
  type AttributoScheda,
  type MisurazioneRiga,
  type ProvenienzaMisurazione,
  oggi,
} from './elenchi.js';
import { useConferma } from '../comuni/Conferma.js';

export function AttributiGiocatore() {
  const { id } = useParams<{ id: string }>();
  const { dati, errore, stato, ricarica } = useDati<Dati>(`/giocatori/${id}/attributi`);

  if (stato === 403 || stato === 404) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  // Solo cio che ha dati: un attributo mai misurato non occupa spazio.
  const misurati = dati.attributi.filter((a) => a.misurazioni.length > 0);

  return (
    <div className="flex flex-col gap-4">
      {dati.puoScrivere && (
        <Nuova giocatoreId={id!} attributi={dati.attributi} onSalvato={ricarica} />
      )}
      {misurati.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessuna misurazione</EmptyTitle>
            <EmptyDescription>Ancora nessuna rilevazione per questo giocatore.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-4">
          {FAMIGLIE_ATTRIBUTO.map((f) => {
            const attributi = misurati.filter((a) => a.famiglia === f);
            if (attributi.length === 0) return null;
            return (
              <Card key={f} className="bg-muted">
                <CardHeader>
                  <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
                    <IconaFamiglia famiglia={f} aria-hidden className="size-5" />
                    <h2>{ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}</h2>
                  </CardTitle>
                </CardHeader>
                {/* Gli attributi in due colonne quando c'e spazio; uno aperto non allunga il vicino. */}
                <CardContent className="grid items-start gap-2 px-2 @xl:px-(--card-spacing) @3xl:grid-cols-2">
                  {attributi.map((a) => (
                    <Attributo key={a.id} attributo={a} onCambio={ricarica} />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Chiuso dice l'ultimo valore e quando; aperto, lo storico con le correzioni. */
function Attributo({
  attributo: a,
  onCambio,
}: {
  attributo: AttributoScheda;
  onCambio: () => Promise<void>;
}) {
  const ultima = a.misurazioni[0]!;
  return (
    <Collapsible className="rounded-lg border bg-card">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="group h-auto w-full justify-between px-3 py-2">
          <span className="flex items-center gap-2">
            <ChevronRightIcon className="transition-transform group-data-[state=open]:rotate-90" />
            {a.nome}
          </span>
          <span className="flex items-center gap-2">
            <span className="text-xs font-normal text-muted-foreground">
              {dataBreve(ultima.data)}
            </span>
            <Badge className={cn('min-w-9 tabular-nums', coloreValore(ultima.valore))}>
              {ultima.valore}
            </Badge>
          </span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-3 pb-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Valore</TableHead>
              <TableHead>Autore</TableHead>
              <TableHead>Da un test</TableHead>
              {/* Le azioni restano a destra mentre le altre colonne scorrono. */}
              <TableHead className="sticky right-0 bg-card">
                <span className="sr-only">Azioni</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {a.misurazioni.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{dataBreve(m.data)}</TableCell>
                <TableCell className="tabular-nums">{m.valore}</TableCell>
                <TableCell>{m.autore ?? '—'}</TableCell>
                <TableCell className="min-w-48 whitespace-normal">
                  {m.esecuzione ? <Provenienza da={m.esecuzione} /> : '—'}
                </TableCell>
                <TableCell className="sticky right-0 bg-card">
                  {m.puoModificare && (
                    <div className="flex justify-end">
                      <AzioniMisurazione attributo={a} riga={m} onSalvato={onCambio} />
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * «Sprint 30 m · 4,5 s», con la misura se il test ne ha più d'una («Sprint 100 m ·
 * Parziale 10 m · 1,85 s»), e il rimando all'esecuzione per chi può aprirla.
 */
function Provenienza({ da }: { da: ProvenienzaMisurazione }) {
  const { utente } = useContesto();
  const testo = provenienzaAParole(da);
  if (!puo(utente.assegnazioni, 'misurazione.leggi', da.squadraStagioneId)) return testo;
  return (
    <Button asChild variant="link" className="h-auto p-0 text-left whitespace-normal">
      <Link to={`/sq/${da.squadraStagioneId}/test/${da.testId}/esecuzioni/${da.id}`}>{testo}</Link>
    </Button>
  );
}

function Nuova({
  giocatoreId,
  attributi,
  onSalvato,
}: {
  giocatoreId: string;
  attributi: AttributoScheda[];
  onSalvato: () => Promise<void>;
}) {
  const attivi = attributi.filter((a) => a.attivo);
  const [aperto, setAperto] = useState(false);
  if (attivi.length === 0) return null;
  return (
    <div className="flex justify-end">
      <PulsanteNuovo etichetta="Nuova misurazione" onClick={() => setAperto(true)} />
      <FormMisurazione
        aperto={aperto}
        onAperto={setAperto}
        attributi={attivi}
        iniziale={{ attributoId: attivi[0]!.id, valore: 60, data: oggi() }}
        onInvia={(dati) =>
          chiama(`/giocatori/${giocatoreId}/misurazioni`, { metodo: 'POST', corpo: dati })
        }
        onSalvato={onSalvato}
        titolo="Nuova misurazione"
      />
    </div>
  );
}

/** Correggi ed elimina di una misurazione, nel menu «⋯» della riga. */
function AzioniMisurazione({
  attributo,
  riga,
  onSalvato,
}: {
  attributo: AttributoScheda;
  riga: MisurazioneRiga;
  onSalvato: () => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();
  const elimina = async () => {
    const si = await chiedi({
      titolo: 'Eliminare la misurazione?',
      testo: 'Il valore esce dallo storico del giocatore e dalle sue medie.',
      azione: 'Elimina',
      distruttiva: true,
    });
    if (!si) return;
    setInCorso(true);
    try {
      await chiama(`/misurazioni/${riga.id}`, { metodo: 'DELETE' });
      await onSalvato();
      toast.success('Misurazione eliminata.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };
  return (
    <>
      <MenuAzioni
        riga
        etichetta="Azioni sulla misurazione"
        voci={[
          { etichetta: 'Correggi', Icona: PencilIcon, onSelect: () => setAperto(true) },
          {
            etichetta: 'Elimina',
            Icona: Trash2Icon,
            disabled: inCorso,
            onSelect: () => void elimina(),
            distruttiva: true,
          },
        ]}
      />
      <FormMisurazione
        aperto={aperto}
        onAperto={setAperto}
        attributi={[attributo]}
        iniziale={{ attributoId: attributo.id, valore: riga.valore, data: riga.data }}
        onInvia={(dati) => chiama(`/misurazioni/${riga.id}`, { metodo: 'PUT', corpo: dati })}
        onSalvato={onSalvato}
        titolo="Correggi misurazione"
      />
      {conferma}
    </>
  );
}

function FormMisurazione({
  aperto,
  onAperto,
  attributi,
  iniziale,
  onInvia,
  onSalvato,
  titolo,
}: {
  aperto: boolean;
  onAperto: (v: boolean) => void;
  attributi: AttributoScheda[];
  iniziale: { attributoId: string; valore: number; data: string };
  onInvia: (dati: { attributoId: string; valore: number; data: string }) => Promise<unknown>;
  onSalvato: () => Promise<void>;
  titolo: string;
}) {
  const [attributoId, setAttributoId] = useState(iniziale.attributoId);
  const [valore, setValore] = useState(String(iniziale.valore));
  const [data, setData] = useState(iniziale.data);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setAttributoId(iniziale.attributoId);
    setValore(String(iniziale.valore));
    setData(iniziale.data);
    setErrori({});
    setErroreServer(null);
  }, [aperto, iniziale.attributoId, iniziale.valore, iniziale.data]);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = misurazioneSchema.safeParse({
      attributoId,
      valore: Number(valore),
      data,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await onInvia(esito.data);
      await onSalvato();
      toast.success('Misurazione salvata.');
      onAperto(false);
    } catch (err) {
      setErroreServer(messaggio(err));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Dialog open={aperto} onOpenChange={onAperto}>
      <DialogContent>
        <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
          <DialogHeader>
            <DialogTitle>{titolo}</DialogTitle>
            <DialogDescription>Data e valore della rilevazione.</DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="mis-attributo">Attributo</FieldLabel>
              <Select value={attributoId} onValueChange={setAttributoId}>
                <SelectTrigger id="mis-attributo" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {attributi.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.nome}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field data-invalid={Boolean(errori.valore)}>
              <FieldLabel htmlFor="mis-valore">Valore</FieldLabel>
              <Input
                id="mis-valore"
                type="number"
                inputMode="numeric"
                min={1}
                max={100}
                step={1}
                value={valore}
                aria-invalid={Boolean(errori.valore)}
                onChange={(e) => setValore(e.target.value)}
              />
              <FieldError>{errori.valore}</FieldError>
            </Field>
            <Field data-invalid={Boolean(errori.data)}>
              <FieldLabel htmlFor="mis-data">Data</FieldLabel>
              <CampoData
                id="mis-data"
                valore={data}
                invalido={Boolean(errori.data)}
                onCambia={setData}
              />
              <FieldError>{errori.data}</FieldError>
            </Field>
          </FieldGroup>
          {erroreServer && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Salvataggio non riuscito</AlertTitle>
              <AlertDescription>{erroreServer}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <PulsanteInvio type="submit" inCorso={inCorso}>
              Salva
            </PulsanteInvio>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
